import { cn } from '@/app/utils/cn';
import checkTruthyValue from '@/app/utils/checkTruthyValue';
import { IconCircleChevronRight, IconArrowNarrowRight } from '@tabler/icons-react';
import Image from 'next/image';
import LinkNoPrefetch from '@/components/link-noprefetch';

function PrimaryButton({
    iconPosition = 'right',
    disableClickEffect,
    url,
    urlName,
    children,
    className,
    ariaLabel
}) {
    const twClasses = cn('btn btn-dark text-white hover:text-blue hover:bg-white');
    const twDark = cn(
        `dark:btn-light dark:text-blue-700 dark:btn-light-chevron dark:hover:border-white dark:hover:bg-black dark:hover:text-white`
    );

    return (
        <LinkNoPrefetch
            disableClickEffect={!!disableClickEffect}
            href={url}
            role="button"
            className={cn(twClasses, twDark, className)}
            aria-label={ariaLabel || urlName}>
            {iconPosition === 'left' && <IconCircleChevronRight size={24} className="mr-2" />}
            {children ?? urlName}
            <span className="sr-only"> about {urlName}</span>
            {iconPosition === 'right' && <IconCircleChevronRight size={24} className="mr-2" />}
        </LinkNoPrefetch>
    );
}

function SecondaryButton({
    iconPosition = 'right',
    disableClickEffect,
    url,
    urlName,
    children,
    className,
    ariaLabel
}) {
    const twClasses = cn(
        `btn border-indigo-700 bg-indigo-700 text-white hover:border-indigo-700 hover:text-indigo-700 hover:bg-white`
    );

    const twDark = cn(
        `dark:text-indigo-700 dark:border-white dark:bg-white dark:hover:bg-indigo-700 dark:hover:text-white`
    );

    return (
        <LinkNoPrefetch
            disableClickEffect={!!disableClickEffect}
            href={url}
            role="button"
            className={cn(twClasses, twDark, className)}
            aria-label={ariaLabel || urlName}>
            {iconPosition === 'left' && <IconCircleChevronRight size={24} className="mr-2" />}
            {children ?? urlName}
            <span className="sr-only"> about {urlName}</span>
            {iconPosition === 'right' && <IconCircleChevronRight size={24} className="mr-2" />}
        </LinkNoPrefetch>
    );
}

export function LinkButton({
    disableClickEffect,
    url,
    urlName,
    children,
    className,
    ariaLabel,
    linkBtnAsStandartLink = false
}) {
    const twClasses = cn(
        `text-blue-700 bg-transparent border-blue-700 w-fit btn dark:text-blue-700 hover:bg-blue-700 hover:text-white`
    );
    const twDark = cn(
        `dark:text-white dark:border-white dark:hover:bg-white dark:hover:text-blue-700`
    );

    const commonProps = {
        href: url,
        className: cn(twClasses, twDark, className, 'no-underline'),
        'aria-label': ariaLabel || urlName
    };

    if (!!linkBtnAsStandartLink) {
        return (
            <a {...commonProps} role="button">
                {children ?? urlName}
                <span className="sr-only"> about {urlName}</span>
            </a>
        );
    }

    return (
        <LinkNoPrefetch disableClickEffect={!!disableClickEffect} {...commonProps} role="button">
            {children ?? urlName}
            <span className="sr-only"> about {urlName}</span>
        </LinkNoPrefetch>
    );
}

export function LinkWithIcon({
    iconPosition = 'right',
    disableClickEffect,
    url,
    urlName,
    children,
    className,
    ariaLabel,
    icon
}) {
    const twClasses = `text-section-color bg-transparent w-fit btn dark:text-section !border-0`;
    const twDark = `dark:text-white dark:hover:text-section-color !border-0`;

    let iconElement = null;
    if (typeof icon === 'string') {
        iconElement = <Image src={icon} alt="image" width={20} height={20} sizes="20px" />;
    } else if (icon && (icon.idPath || icon.identifier)) {
        iconElement = (
            <Image
                src={icon.idPath || icon.identifier}
                alt="image"
                width={20}
                height={20}
                sizes="20px"
            />
        );
    } else {
        iconElement = <IconArrowNarrowRight size={20} className="mr-2" />;
    }

    return (
        <LinkNoPrefetch
            disableClickEffect={!!disableClickEffect}
            href={url}
            role="button"
            className={cn(twClasses, twDark, className)}
            aria-label={ariaLabel || urlName}>
            {iconPosition === 'left' && iconElement}
            {children ?? urlName}
            <span className="sr-only"> about {urlName}</span>
            {iconPosition === 'right' && iconElement}
        </LinkNoPrefetch>
    );
}

export function CallToActionButton({
    buttonType,
    children,
    className,
    position = 'left',
    extraSpace,
    noPadding = false,
    ...props
}) {
    const buttons = {
        primary: PrimaryButton,
        secondary: SecondaryButton,
        link: LinkButton,
        linkWithIcon: LinkWithIcon
    };

    const alignment = {
        left: 'ml-0 mr-auto',
        center: 'mx-auto',
        right: 'ml-auto mr-0'
    };

    const spacing = {
        top: '!mt-12',
        bottom: '!mb-12',
        both: '!my-12'
    };

    const Button = buttons[buttonType] ?? PrimaryButton;
    let paddingClass = '';
    if (checkTruthyValue(noPadding)) {
        paddingClass = 'px-0';
    }
    const commonClasses = cn(
        `mx-auto flex gap-2 justify-center items-center w-fit border border-2 border-solid transition-colors duration-300`,
        paddingClass,
        alignment[position],
        spacing[extraSpace]
    );

    return (
        <Button {...props} className={cn(commonClasses, className)}>
            {children}
        </Button>
    );
}
